import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

const MAX_SIDE = 300; // px — output capped to 300x300
const OUTPUT_MIME = "image/png";

function resizeToDataURL(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = Math.min(MAX_SIDE / img.width, MAX_SIDE / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const data_url = canvas.toDataURL(OUTPUT_MIME, 0.92);
        URL.revokeObjectURL(url);
        resolve(data_url);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };
    img.src = url;
  });
}

export default function LogoUploader({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => ref.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload a PNG, JPG, or WebP image");
      return;
    }
    setBusy(true);
    try {
      const data_url = await resizeToDataURL(f);
      onChange(data_url);
    } catch (err) {
      toast.error(err.message || "Could not process image");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div className="flex items-center gap-4">
      <div
        className="h-24 w-24 shrink-0 rounded-md border border-border bg-gray-50 overflow-hidden flex items-center justify-center"
        data-testid="logo-preview"
      >
        {value ? (
          <img src={value} alt="Logo preview" className="h-full w-full object-contain" />
        ) : (
          <span className="text-[10px] font-mono text-gray-400 text-center px-2">No logo<br/>uploaded</span>
        )}
      </div>
      <div className="space-y-2">
        <input
          ref={ref}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFile}
          className="hidden"
          data-testid="logo-file-input"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={pick}
            disabled={busy}
            className="rounded-[35px] shadow-sm hover:shadow-md"
            data-testid="logo-upload-button"
          >
            <Upload size={14} className="mr-1" /> {busy ? "Processing…" : value ? "Replace logo" : "Upload logo"}
          </Button>
          {value && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clear}
              className="rounded-[35px]"
              data-testid="logo-remove-button"
            >
              <X size={14} className="mr-1" /> Remove
            </Button>
          )}
        </div>
        <p className="text-[11px] text-gray-500">PNG/JPG/WebP — resized automatically to max 300×300.</p>
      </div>
    </div>
  );
}
